import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, ArrowRight, ArrowLeft, Camera, Settings2, ScanFace, Loader2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { PageHeader } from "@/components/dashboard/ui";

const TYPES = ["Wedding", "Engagement", "Pre-Wedding", "Birthday", "Corporate", "Reception", "Anniversary", "Other"];
const STEPS = [{ icon: Camera, label: "Event Details" }, { icon: Settings2, label: "Gallery Settings" }, { icon: ScanFace, label: "AI Settings" }];

function Field({ label, children }) {
  return <div><label className="text-sm text-secondary-foreground">{label}</label>{children}</div>;
}
const inputCls = "mt-1.5 w-full bg-secondary border border-input rounded-xl px-4 py-3 text-white outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors";

function Toggle({ label, desc, value, onChange, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={() => onChange(!value)} className="w-full flex items-center justify-between rounded-xl border border-border bg-black/40 p-4 text-left hover:border-gold/25 transition-colors">
      <div><div className="text-white text-sm">{label}</div><div className="text-xs text-muted-foreground mt-0.5">{desc}</div></div>
      <div className={`w-11 h-6 rounded-full p-0.5 transition-colors ${value ? "bg-gold" : "bg-white/15"}`}><div className={`w-5 h-5 rounded-full bg-black transition-transform ${value ? "translate-x-5" : ""}`} /></div>
    </button>
  );
}

export default function CreateEvent() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    name: "", event_type: "Wedding", client_name: "", event_date: "", venue: "", description: "",
    cover_image: "", privacy: "private", download_permission: true, watermark: false, expiry_date: "",
    ai_face_search: true, ai_selfie_search: true, ai_similar_faces: true,
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const next = () => {
    if (step === 0 && (!f.name || !f.client_name || !f.event_date)) { toast.error("Please fill event name, client and date"); return; }
    setStep((s) => Math.min(s + 1, 2));
  };

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/events", f);
      toast.success("Event created — share the QR with your client!");
      navigate(`/app/events/${data.event.id}`);
    } catch (e) {
      toast.error(apiErr(e));
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <PageHeader title="Create Event" subtitle="Set up a beautiful gallery for your client in three quick steps." />

      <div className="flex items-center justify-between mb-8">
        {STEPS.map((s, i) => (
          <div key={i} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center">
              <div className={`w-11 h-11 rounded-full flex items-center justify-center border transition-colors ${i <= step ? "bg-gold border-gold text-black" : "border-border text-muted-foreground"}`}>
                {i < step ? <Check className="w-5 h-5" /> : <s.icon className="w-5 h-5" />}
              </div>
              <span className={`text-xs mt-2 ${i <= step ? "text-white" : "text-muted-foreground"}`}>{s.label}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`h-px flex-1 mx-3 ${i < step ? "bg-gold" : "bg-border"}`} />}
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-border bg-black/40 p-6 sm:p-8 animate-fade-up" key={step}>
        {step === 0 && (
          <div className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Event Name *"><input data-testid="event-name" value={f.name} onChange={(e) => set("name", e.target.value)} className={inputCls} placeholder="Rahul & Priya Wedding" /></Field>
              <Field label="Event Type">
                <select data-testid="event-type" value={f.event_type} onChange={(e) => set("event_type", e.target.value)} className={inputCls}>
                  {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Client Name *"><input data-testid="event-client" value={f.client_name} onChange={(e) => set("client_name", e.target.value)} className={inputCls} placeholder="Priya Sharma" /></Field>
              <Field label="Event Date *"><input data-testid="event-date" type="date" value={f.event_date} onChange={(e) => set("event_date", e.target.value)} className={inputCls} /></Field>
            </div>
            <Field label="Venue"><input data-testid="event-venue" value={f.venue} onChange={(e) => set("venue", e.target.value)} className={inputCls} placeholder="The Leela Palace, New Delhi" /></Field>
            <Field label="Description"><textarea data-testid="event-desc" value={f.description} onChange={(e) => set("description", e.target.value)} rows={3} className={inputCls} placeholder="A short, warm description of the event…" /></Field>
            <Field label="Cover Image URL (optional)"><input data-testid="event-cover" value={f.cover_image} onChange={(e) => set("cover_image", e.target.value)} className={inputCls} placeholder="https://…" /></Field>
          </div>
        )}
        {step === 1 && (
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Gallery Privacy">
                <select data-testid="event-privacy" value={f.privacy} onChange={(e) => set("privacy", e.target.value)} className={inputCls}>
                  <option value="private">Private (link/QR only)</option>
                  <option value="public">Public</option>
                </select>
              </Field>
              <Field label="Expiry Date (optional)"><input data-testid="event-expiry" type="date" value={f.expiry_date} onChange={(e) => set("expiry_date", e.target.value)} className={inputCls} /></Field>
            </div>
            <Toggle testid="toggle-download" label="Allow downloads" desc="Let clients download photos from the gallery" value={f.download_permission} onChange={(v) => set("download_permission", v)} />
            <Toggle testid="toggle-watermark" label="Apply watermark" desc="Overlay a PMR Events watermark on gallery photos" value={f.watermark} onChange={(v) => set("watermark", v)} />
          </div>
        )}
        {step === 2 && (
          <div className="space-y-4">
            <div className="rounded-xl border border-gold/20 bg-gold/5 p-4 text-sm text-secondary-foreground flex items-start gap-3">
              <ScanFace className="w-5 h-5 text-gold shrink-0 mt-0.5" />
              Clients see a consent notice before any selfie is processed. Face data is encrypted and can be deleted anytime.
            </div>
            <Toggle testid="toggle-face" label="Enable Face Search" desc="Extract face embeddings from every uploaded photo" value={f.ai_face_search} onChange={(v) => set("ai_face_search", v)} />
            <Toggle testid="toggle-selfie" label="Enable Selfie Search" desc="Let clients find themselves by uploading a selfie" value={f.ai_selfie_search} onChange={(v) => set("ai_selfie_search", v)} />
            <Toggle testid="toggle-similar" label="Similar Face Matching" desc="Group and surface photos with similar faces" value={f.ai_similar_faces} onChange={(v) => set("ai_similar_faces", v)} />
          </div>
        )}

        <div className="flex items-center justify-between mt-8">
          <button data-testid="wizard-back" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-white disabled:opacity-30"><ArrowLeft className="w-4 h-4" /> Back</button>
          {step < 2 ? (
            <button data-testid="wizard-next" onClick={next} className="inline-flex items-center gap-2 bg-gold text-black font-medium rounded-full px-6 py-2.5 hover:bg-gold-light transition-colors">Continue <ArrowRight className="w-4 h-4" /></button>
          ) : (
            <button data-testid="wizard-create" onClick={submit} disabled={saving} className="inline-flex items-center gap-2 bg-gold text-black font-medium rounded-full px-6 py-2.5 hover:bg-gold-light transition-colors disabled:opacity-60">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Create Event
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
